import React, { useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import LanguageSwitcher from './LanguageSwitcher';
import LiveClockBadge from './LiveClockBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, CreditCard, Shield, ChevronDown, Key, CalendarDays, Tag } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // Grace delay on close so the mouse can travel from the trigger to the
  // portalised DropdownMenuContent without the menu vanishing mid-move.
  // Without this, moving the mouse ~2px between the trigger and the menu
  // caused a flicker as the mouseleave fired before the menu registered
  // its own mouseenter.
  const userMenuCloseTimer = useRef(null);
  const openUserMenu = () => {
    if (userMenuCloseTimer.current) {
      clearTimeout(userMenuCloseTimer.current);
      userMenuCloseTimer.current = null;
    }
    setUserMenuOpen(true);
  };
  const scheduleCloseUserMenu = () => {
    if (userMenuCloseTimer.current) clearTimeout(userMenuCloseTimer.current);
    userMenuCloseTimer.current = setTimeout(() => setUserMenuOpen(false), 180);
  };
  const { logo } = useSiteSettings();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isAdmin = user?.role === 'admin' || user?.email === 'dequenne@hotmail.com';

  const navLinks = [
    { href: '/plan', label: t('nav.plan') },
    { href: '/calendrier', label: t('nav.calendar') },
    { href: '/evenements', label: t('nav.events') },
    ...(isAdmin ? [{ href: '/admin', label: t('nav.admin') }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav data-testid="navbar" className="sticky top-0 z-50 bg-[#1A3C34] shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <img
              src={logo}
              alt="Flexi-location"
              className="h-8 w-auto rounded"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.href.slice(1)}`}
                className={`px-4 py-2 text-base font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                  isActive(link.href)
                    ? 'bg-white/15 text-white'
                    : 'text-white/75 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-2.5">
            <LiveClockBadge />
            {user ? (
              <>
                <LanguageSwitcher />
                <div
                  onMouseEnter={openUserMenu}
                  onMouseLeave={scheduleCloseUserMenu}
                  className="relative"
                >
                <DropdownMenu open={userMenuOpen} onOpenChange={setUserMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-trigger"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-all"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-white/20" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#C77D63] flex items-center justify-center">
                        <span className="text-xs text-white font-bold">
                          {user.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <span className="text-sm font-medium">{user.name}</span>
                    <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-52 mt-1"
                  onMouseEnter={openUserMenu}
                  onMouseLeave={scheduleCloseUserMenu}
                >
                  <DropdownMenuItem
                    data-testid="nav-dashboard"
                    onClick={() => navigate('/dashboard')}
                    className="cursor-pointer"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {t('nav.dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="nav-my-access"
                    onClick={() => navigate('/mes-acces')}
                    className="cursor-pointer"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    {t('nav.myAccess', 'Mes accès')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="nav-my-data"
                    onClick={() => navigate('/dashboard?section=personal-info')}
                    className="cursor-pointer"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {t('nav.myData', 'Mes données')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="nav-billing"
                    onClick={() => navigate('/dashboard?section=billing')}
                    className="cursor-pointer"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {t('nav.myInvoices', 'Mes factures')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="nav-my-events"
                    onClick={() => navigate('/dashboard?section=events')}
                    className="cursor-pointer"
                  >
                    <CalendarDays className="w-4 h-4 mr-2" />
                    {t('nav.myEvents', 'Mes événements')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="nav-subscriptions-menu"
                    onClick={() => navigate('/abonnements')}
                    className="cursor-pointer"
                  >
                    <Tag className="w-4 h-4 mr-2" />
                    {t('nav.subscriptions')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="nav-logout"
                    onClick={handleLogout}
                    className="cursor-pointer text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <LanguageSwitcher />
                <button
                  data-testid="nav-login-btn"
                  onClick={() => navigate('/auth')}
                  className="text-sm font-medium text-white/70 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/10 transition-all"
                >
                  {t('nav.login')}
                </button>
                <button
                  data-testid="nav-register-btn"
                  onClick={() => navigate('/auth?mode=register')}
                  className="text-sm font-bold bg-[#C77D63] text-white hover:bg-[#b06a51] rounded-lg h-9 px-5 transition-all shadow-sm"
                >
                  {t('nav.register')}
                </button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2 text-white/80 hover:text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden py-3 border-t border-white/10">
            <div className="px-4 pb-3">
              <LiveClockBadge variant="mobile" />
            </div>
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`block px-4 py-2.5 text-sm font-medium rounded-lg ${
                  isActive(link.href) ? 'bg-white/15 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="block px-4 py-2.5 text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
                  onClick={() => setMobileOpen(false)}
                >
                  Tableau de bord
                </Link>
                <Link
                  to="/mes-acces"
                  className="block px-4 py-2.5 text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 rounded-lg"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-nav-my-access"
                >
                  {t('nav.myAccess', 'Mes accès')}
                </Link>
                <button
                  onClick={() => { handleLogout(); setMobileOpen(false); }}
                  className="block w-full text-left px-4 py-2.5 text-sm font-medium text-red-400 hover:bg-red-500/10 rounded-lg"
                >
                  Deconnexion
                </button>
              </>
            ) : (
              <div className="px-4 pt-3 flex flex-col gap-2">
                <button
                  onClick={() => { navigate('/auth'); setMobileOpen(false); }}
                  className="w-full text-left text-white/70 hover:text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-white/10"
                >
                  Connexion
                </button>
                <button
                  onClick={() => { navigate('/auth?mode=register'); setMobileOpen(false); }}
                  className="w-full bg-[#C77D63] text-white font-bold py-2.5 px-4 rounded-lg text-sm hover:bg-[#b06a51]"
                >
                  S'inscrire
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
